
import Vue from "vue";
import TextField from "@/components/molecules/TextField.vue";
import ImageSelector from "@/components/molecules/ImageSelector.vue";
import { submitImage } from "@/lib/image-save";
import {
  getTranslatedGenreList,
  getTranslatedCategoryList,
  getTranslatedWorkStatusList,
} from "@/lib/utils/novelSettingsHelper";
import { Novel } from "@/lib/models";
import { Dialog } from "@/lib/utils";
import DialogVue from "@/components/ui/Dialog.vue";
import isMobile from "ismobilejs";
import { handleShortcutKeys, TriggerKey } from "@/lib/utils/keyboardShortcuts";
import Onboarding from "@/components/molecules/OnBoarding.vue";
import { NovelSetting } from "@/lib/models/novel";

type NovelParam = {
  id: string;
  name: string;
  image: string;
};

interface Data {
  novel: Novel | null;
  image?: string;
  genre?: NovelParam | null;
  category?: NovelParam[] | null;
  workStatus?: NovelParam | null;
  unbindShortcutKeys: (() => void) | null;
  showFirstWorkStatusHighlight: boolean;
  // 初期値（比較用）
  initialTitle: string;
  initialDescription: string;
  initialGenre: string | null;
  initialCategory: string[] | null;
  initialWorkStatus: string | null;
  initialImage: string | undefined;
  isInitialized: boolean; // 初期化が完了したかどうか
}

interface Methods {
  change(): void;
  initialize(): void;
  imagePath(): string | null;
  submit(): Promise<void>;
  onChangeGenre(event: Event): void;
  onChangeWorkStatus(event: Event): void;
  addCategory(item: NovelParam): void;
  removeCategory(item: NovelParam): void;
  showErrorDialog(message: string): void;
  onboardingOutsideClicked(): void;
}

interface Computed {
  isPC: boolean;
  hasDisplayedWorkStatusHighlightPopup: boolean;
  translatedGenreList: NovelParam[];
  translatedCategoryList: NovelParam[];
  translatedWorkStatusList: NovelParam[];
  currentWorkStatus: NovelSetting;
  workStatusIsNotCompleted: boolean;
  validationMessage: string;
  canSubmit: boolean;
  changed: boolean;
  categoryIds: string[];
  availableCategories: NovelParam[];
}

interface Props {
  novelId: string;
}

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    TextField,
    ImageSelector,
    Onboarding,
  },
  props: {
    novelId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      novel: null,
      image: undefined,
      genre: null,
      category: null,
      workStatus: null,
      unbindShortcutKeys: null,
      showFirstWorkStatusHighlight: false,
      // 初期値（比較用）
      initialTitle: "",
      initialDescription: "",
      initialGenre: null as string | null,
      initialCategory: null as string[] | null,
      initialWorkStatus: null as string | null,
      initialImage: undefined as string | undefined,
      isInitialized: false, // 初期化が完了したかどうか
    };
  },
  async created() {
    const { initialize } = this;
    initialize();
  },
  beforeDestroy() {
    // コンポーネントが破棄される前に保存ボタンのショートカットキーを解除
    if (this.unbindShortcutKeys) this.unbindShortcutKeys();
  },
  computed: {
    isPC() {
      return !isMobile().any;
    },
    hasDisplayedWorkStatusHighlightPopup() {
      return this.$store.getters["generalModule/hasDisplayedWorkStatusHighlightPopup"];
    },
    translatedGenreList() {
      return getTranslatedGenreList(this.$t);
    },
    translatedCategoryList() {
      return getTranslatedCategoryList(this.$t);
    },
    translatedWorkStatusList() {
      return getTranslatedWorkStatusList(this.$t);
    },
    currentWorkStatus() {
      const { novel } = this;
      return (
        this.translatedWorkStatusList.find((status) => status.id === novel.workStatus) ||
        this.translatedWorkStatusList[0]
      );
    },
    workStatusIsNotCompleted() {
      return this.currentWorkStatus.id !== "completed";
    },
    validationMessage() {
      // 両方nullの場合は、統一メッセージを表示
      if (this.genre === null && this.category === null) {
        return this.$t("novelDetailEditPage.validation.genreCategoryRequired");
      }

      // genreがnullの場合のみ表示
      if (this.genre === null) {
        return this.$t("novelDetailEditPage.validation.genreRequired");
      }
      // categoryがnullの場合のみ表示
      if (this.category === null) {
        return this.$t("novelDetailEditPage.validation.categoryRequired");
      }
      return "";
    },
    canSubmit() {
      return this.novel.title && this.genre !== null && this.category !== null;
    },
    changed() {
      // novelが初期化されていない場合、または初期化が完了していない場合はfalse
      if (!this.novel || !this.isInitialized) {
        return false;
      }
      // 初期値と現在の値を比較して変更を検知
      const currentGenre = this.genre ? this.genre.id : null;
      const currentCategory = this.category ? this.category.map((item) => item.id).sort() : null;
      const initialCategorySorted = this.initialCategory ? [...this.initialCategory].sort() : null;
      const currentWorkStatus = this.workStatus ? this.workStatus.id : null;

      return (
        (this.novel.title || "") !== (this.initialTitle || "") ||
        (this.novel.description || "") !== (this.initialDescription || "") ||
        currentGenre !== this.initialGenre ||
        JSON.stringify(currentCategory) !== JSON.stringify(initialCategorySorted) ||
        currentWorkStatus !== this.initialWorkStatus ||
        this.image !== this.initialImage
      );
    },
    categoryIds() {
      return this.category ? this.category.map((item) => item.id) : [];
    },
    availableCategories() {
      // 選択されていないカテゴリのリスト
      const selectedIds = this.categoryIds;
      return this.translatedCategoryList.filter((item) => !selectedIds.includes(item.id));
    },
  },
  methods: {
    async initialize() {
      const { novelId } = this;
      const { getters } = this.$store;
      const novel = getters["novelModule/novel"](novelId) as Novel;
      this.novel = { ...novel };

      // 保存ボタンのショートカットキーの有効化と解除関数の取得
      this.unbindShortcutKeys = handleShortcutKeys([
        { trigger: TriggerKey.Meta, keys: ["s"], callback: this.submit },
        { trigger: TriggerKey.Ctrl, keys: ["s"], callback: this.submit },
      ]);

      const { genre, category, workStatus, image } = novel;
      if (genre) {
        this.genre = this.translatedGenreList.find((item: NovelParam) => item.id === genre) ?? null;
      }
      if (category) {
        this.category = this.translatedCategoryList.filter((item: NovelParam) => category.includes(item.id));
      }
      if (workStatus) {
        this.workStatus =
          this.translatedWorkStatusList.find((x: NovelParam) => x.id === workStatus) ??
          this.translatedWorkStatusList.find((x: NovelParam) => x.id === "inProgress")!;
      } else {
        this.workStatus = this.translatedWorkStatusList.find((x: NovelParam) => x.id === "inProgress")!;
      }
      if (image) {
        this.image = image;
      }
      // 初期値を保存（比較用）- すべての値が設定された後に同期で実行
      this.initialTitle = this.novel.title || "";
      this.initialDescription = this.novel.description || "";
      this.initialGenre = this.genre ? this.genre.id : null;
      this.initialCategory = this.category ? this.category.map((item) => item.id) : null;
      this.initialWorkStatus = this.workStatus ? this.workStatus.id : null;
      this.initialImage = this.image;
      // 初期化完了フラグを設定
      this.isInitialized = true;
      // 初期化時は編集状態をリセット
      this.setModified(false);
      // ポップアップ表示（初回かつ、制作状況が制作完了以外の場合）
      if (!this.hasDisplayedWorkStatusHighlightPopup && this.workStatusIsNotCompleted) {
        this.showFirstWorkStatusHighlight = true;

        // 次回以降ポップアップを非表示にする
        await this.$store.dispatch("generalModule/setDisplayedWorkStatusHighlightPopup", true);
      }
    },
    change() {
      // changedはcomputedプロパティで自動的に計算される
      // ただし、setModifiedは手動で呼び出す必要がある
      if (this.changed) {
        this.setModified(true);
      } else {
        this.setModified(false);
      }
    },
    imagePath() {
      const { novel, novelId } = this;

      if (!novel) {
        return null;
      }

      const { image } = this;
      if (!image) {
        return null;
      }

      if (image.startsWith("file:")) {
        return `file:novels/${novelId}/cover.jpg`;
      }

      if (image.startsWith("preset:")) {
        return image;
      }

      return null;
    },
    async submit() {
      const { novelId, novel, showErrorDialog } = this;

      // 保存条件を満たしていない場合は処理を中断
      if (!this.canSubmit) {
        return;
      }

      if (!novel) {
        showErrorDialog(this.$t("novelDetailEditPage.error.cannotGetWork"));
        return;
      }

      const { title, description } = novel;
      const { genre, category, workStatus, image } = this;
      let genreId: string | undefined;
      let categoryIds: string[] | undefined;
      let workStatusId: string | undefined;

      if (genre) {
        genreId = genre.id;
      }

      if (category) {
        categoryIds = category.map((x: NovelParam) => x.id);
      }

      if (workStatus) {
        workStatusId = workStatus.id;
      }

      await submitImage(`novels/${novelId}/cover.jpg`);
      const { dispatch } = this.$store;
      await dispatch("novelModule/updateNovel", {
        novelId,
        title,
        image,
        description,
        genre: genreId,
        category: categoryIds,
        workStatus: workStatusId,
      });
      // 保存後に初期値を更新
      this.initialTitle = this.novel.title || "";
      this.initialDescription = this.novel.description || "";
      this.initialGenre = this.genre ? this.genre.id : null;
      this.initialCategory = this.category ? this.category.map((item) => item.id) : null;
      this.initialWorkStatus = this.workStatus ? this.workStatus.id : null;
      this.initialImage = this.image;
      this.setModified(false);
      // 制作状況が「制作完了」の場合、エージェント同意を促すダイアログ表示の為のクエリパラメータを付与
      this.$router.push({
        name: "detail",
        params: { novelId },
        query: this.workStatus.id === "completed" ? { workStatus: this.workStatus.id } : undefined,
      });

      this.$notify({
        title: this.$t("novelDetailEditPage.success.saved"),
        type: "success",
      });
    },
    onChangeGenre(event: Event) {
      const target = event.target as HTMLSelectElement;
      const { value } = target;
      if (value === "") {
        this.genre = null;
      } else {
        const item = this.translatedGenreList.find((g) => g.id === value);
        if (item) this.genre = item;
      }
      this.$nextTick(() => {
        this.change();
      });
    },
    // updateCategorySelectStateメソッドは不要になりました（Chips UIに変更したため）
    addCategory(item: NovelParam) {
      // 最大3つまで制限
      if (this.category && this.category.length >= 3) {
        return;
      }
      // カテゴリを追加
      if (!this.category) {
        this.category = [item];
      } else {
        // 既に選択されている場合は追加しない
        if (this.category.some((c) => c.id === item.id)) {
          return;
        }
        this.category = [...this.category, item];
      }
      this.change();
    },
    removeCategory(item: NovelParam) {
      // カテゴリを削除
      if (!this.category) {
        return;
      }
      this.category = this.category.filter((c) => c.id !== item.id);
      if (this.category.length === 0) {
        this.category = null;
      }
      this.change();
    },
    onChangeWorkStatus(event: Event) {
      const target = event.target as HTMLSelectElement;
      const { value } = target;
      const item = this.translatedWorkStatusList.find((s) => s.id === value);
      if (item) {
        this.workStatus = item;
      } else {
        this.workStatus = this.translatedWorkStatusList.find((x) => x.id === "inProgress")!;
      }
      this.$nextTick(() => {
        this.change();
      });
    },
    showErrorDialog(message: string) {
      const confirmDialog = new Dialog(DialogVue);
      const options = {
        title: this.$t("novelDetailEditPage.error.title"),
        content: message,
      };

      confirmDialog.show(options);
    },
    onboardingOutsideClicked() {
      this.showFirstWorkStatusHighlight = false;
    },
  },
});
